/**
 * PWA Settings Component
 * Allows users to control PWA features like notifications, offline mode, etc.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Button,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useAuth } from '../contexts/AuthContext';
import {
  requestNotificationPermission,
  disableNotifications,
  areNotificationsEnabled
} from '../utils/notificationHelper';

export default function PWASettings() {
  const { currentUser } = useAuth();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isStandalone, setIsStandalone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // Check if installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check notification status
    setNotificationsEnabled(areNotificationsEnabled());

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleNotificationToggle = async (event) => {
    const enabled = event.target.checked;
    setLoading(true);
    setMessage(null);

    try {
      if (enabled) {
        const result = await requestNotificationPermission(currentUser?.uid);
        if (result.success) {
          setNotificationsEnabled(true);
          setMessage({ type: 'success', text: '✅ Notifications enabled! You\'ll receive updates about tasks, messages, and more.' });
        } else {
          setMessage({ type: 'error', text: `❌ Could not enable notifications: ${result.error}` });
        }
      } else {
        const result = await disableNotifications(currentUser?.uid);
        if (result.success) {
          setNotificationsEnabled(false);
          setMessage({ type: 'success', text: '🔕 Notifications disabled.' });
        }
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleInstallPrompt = () => {
    if (isStandalone) {
      setMessage({ type: 'info', text: 'App is already installed!' });
    } else {
      setMessage({ 
        type: 'info', 
        text: 'Look for the install button in your browser menu, or wait for the install prompt to appear.' 
      });
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneAndroidIcon />
          App Settings
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {/* Installation Status */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Installation Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isStandalone ? (
              <>
                <Chip
                  icon={<PhoneAndroidIcon />}
                  label="Installed as App"
                  color="success"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  You're using the installed version
                </Typography>
              </>
            ) : (
              <>
                <Chip
                  icon={<PhoneAndroidIcon />}
                  label="Using in Browser"
                  size="small"
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleInstallPrompt}
                >
                  How to Install
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Notifications */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={notificationsEnabled}
                onChange={handleNotificationToggle}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {notificationsEnabled ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
                <Box>
                  <Typography variant="body1">Push Notifications</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Get notified about new tasks, messages, and updates
                  </Typography>
                </Box>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Online/Offline Status */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Connection Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isOnline ? (
              <>
                <CloudDoneIcon color="success" />
                <Typography variant="body2">Online - All features available</Typography>
              </>
            ) : (
              <>
                <CloudOffIcon color="warning" />
                <Typography variant="body2">Offline - Limited features</Typography>
              </>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            The app caches data for offline use. Some features require an internet connection.
          </Typography>
        </Box>

        {/* Beta Features Notice */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>🚀 PWA Features</strong>
            <br />
            • Install on home screen for quick access
            <br />
            • Works offline with cached data
            <br />
            • Push notifications for important updates
            <br />
            • Faster loading with background sync
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
}












